import { FC, useMemo, useState } from 'react';

import { GiftCouponRedeemStatus } from '@shared/api/giftCoupon/giftCoupon.interface';
import { getGiftCsMemoLog } from '@shared/api/giftCsMemo/giftCsMemo.controller';
import { useGiftCsMemoLogQuery } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import {
  GiftCsMemoLogItem,
  GiftCsMemoLogParams,
  GiftCsMemoLogTargetFilter,
} from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { Loading } from '@shared/components/Loading';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';

import { CsMemoModal } from '../components/csMemo';
import {
  redeemStatusColor,
  redeemStatusLabel,
} from '../components/couponDetail/constants';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import { exportCsMemoLogExcel } from '../utils/csMemoLogExcel';

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

const fmtDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  return n.length === 11 ? `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}` : phone;
};

const TARGET_FILTERS: Array<{ value: GiftCsMemoLogTargetFilter; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'order', label: '주문' },
  { value: 'coupon', label: '쿠폰' },
  { value: 'delivery', label: '배송' },
];

const unitBadge = (item: GiftCsMemoLogItem) => {
  if (item.giftCoupon) return { label: '쿠폰', className: 'bg-blue-100 text-blue-700' };
  if (item.giftDelivery)
    return { label: '배송', className: 'bg-teal-100 text-teal-700' };
  return { label: '주문', className: 'bg-gray-100 text-gray-600' };
};

const couponCode = (item: GiftCsMemoLogItem) =>
  item.coupon?.fullCode ??
  [item.coupon?.code, item.coupon?.seq].filter(Boolean).join('-');

/** 서버가 준 교환상태 문자열 → 화면 라벨·색 (모르는 값이면 원문 그대로) */
const redeemBadge = (status: string | undefined) => {
  if (!status) return null;
  const key = status as GiftCouponRedeemStatus;
  return {
    label: redeemStatusLabel[key] ?? status,
    className: redeemStatusColor[key] ?? 'bg-gray-100 text-gray-600',
  };
};

/** 메모장을 열 축 — 셋 다 없는 레거시 메모는 열 수 없다 */
const memoTargetOf = (item: GiftCsMemoLogItem) => {
  if (item.giftCoupon) return { giftCoupon: item.giftCoupon };
  if (item.giftDelivery) return { giftDelivery: item.giftDelivery };
  if (item.giftOrder) return { giftOrder: item.giftOrder };
  return null;
};

const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_PAGES = 10; // 최대 2000건

/**
 * CS 응대 로그 — 남긴 메모를 기간별로 쭉 훑어보는 표.
 * 어떤 문의가 얼마나 들어왔는지 파악하고 응대 매뉴얼을 만드는 용도라 엑셀로도 뽑는다.
 * Admin 전용 메모는 서버가 Admin에게만 내려준다(마리 계정은 목록에서 아예 제외).
 */
export const GiftCsMemoLogPage: FC = function GiftCsMemoLogPage() {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [from, setFrom] = useSessionPersistedState('giftCsMemoLog.from', daysAgo(30));
  const [to, setTo] = useSessionPersistedState('giftCsMemoLog.to', toDateInput(new Date()));
  const [target, setTarget] = useSessionPersistedState<GiftCsMemoLogTargetFilter>(
    'giftCsMemoLog.target',
    'all',
  );
  const [adminOnly, setAdminOnly] = useSessionPersistedState(
    'giftCsMemoLog.adminOnly',
    false,
  );
  const [limit, setLimit] = useSessionPersistedState('giftCsMemoLog.limit', 50);
  // 입력 중인 검색어와 실제 조회에 쓰는 검색어를 분리 (타이핑마다 조회하지 않는다)
  const [searchInput, setSearchInput] = useSessionPersistedState('giftCsMemoLog.q', '');
  const [query, setQuery] = useSessionPersistedState('giftCsMemoLog.appliedQ', '');
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState<GiftCsMemoLogItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const params = useMemo<GiftCsMemoLogParams>(
    () => ({
      from,
      to,
      target,
      ...(query ? { q: query } : {}),
      ...(admin && adminOnly ? { adminOnly: true } : {}),
      page,
      limit,
    }),
    [from, to, target, query, admin, adminOnly, page, limit],
  );

  const { data, isLoading, isFetching } = useGiftCsMemoLogQuery(params);
  const unavailable = data === null;
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // 작성자 분포는 "지금 보고 있는 페이지" 기준 — 전체 집계로 오해하지 않도록 라벨을 붙인다
  const writerCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) map.set(item.byName, (map.get(item.byName) ?? 0) + 1);
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const applyRange = (fromDate: string) => {
    setFrom(fromDate);
    setTo(toDateInput(new Date()));
    setPage(1);
  };

  const runSearch = () => {
    setQuery(searchInput.trim());
    setPage(1);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const all: GiftCsMemoLogItem[] = [];
      for (let p = 1; p <= EXPORT_MAX_PAGES; p += 1) {
        const res = await getGiftCsMemoLog({
          ...params,
          page: p,
          limit: EXPORT_PAGE_SIZE,
        });
        all.push(...res.items);
        if (all.length >= res.total || res.items.length < EXPORT_PAGE_SIZE) break;
      }
      if (all.length === 0) {
        toastError('내보낼 메모가 없습니다.');
        return;
      }
      exportCsMemoLogExcel(all, { from, to });
      if (all.length < total) {
        toastSuccess(
          `${all.length}건 내보냈습니다. (한 번에 최대 ${
            EXPORT_PAGE_SIZE * EXPORT_MAX_PAGES
          }건 — 기간을 나눠 받아주세요)`,
        );
      }
    } catch {
      toastError('엑셀 내보내기에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  const modalTarget = modalItem ? memoTargetOf(modalItem) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold">CS 응대 로그</h1>
            <p className="mt-1 text-sm text-gray-500">
              주문·쿠폰·배송에 남긴 응대 메모 전체 · 기간별 조회
              {admin ? ' · 관리자 전용 메모 포함' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={isExporting || unavailable || total === 0}
            className="rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            title="현재 조회 조건 그대로 엑셀로 내보내기 (응대 매뉴얼용)"
          >
            {isExporting ? '내보내는 중...' : '엑셀 내보내기'}
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', days: 0 },
              { label: '7일', days: 7 },
              { label: '30일', days: 30 },
              { label: '90일', days: 90 },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyRange(daysAgo(preset.days))}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <span className="mx-1 h-5 w-px bg-gray-200" />

          <div className="flex items-center gap-1">
            {TARGET_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setTarget(option.value);
                  setPage(1);
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  target === option.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {admin && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={adminOnly}
                onChange={(e) => {
                  setAdminOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              관리자 전용만
            </label>
          )}

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            placeholder="내용, 작성자, 쿠폰번호, 주문자"
            className="min-w-[220px] flex-1 rounded-lg border px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={runSearch}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            검색
          </button>
          {query && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              초기화
            </button>
          )}
        </div>

        {/* 요약 */}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>
            총 <span className="font-bold text-gray-800">{total}</span>건
          </span>
          {isFetching && <span className="text-indigo-500">불러오는 중...</span>}
          {writerCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">이 페이지 작성자</span>
              {writerCounts.map(([name, count]) => (
                <span
                  key={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {name} {count}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* 목록 */}
      <div className="p-4">
        {isLoading ? (
          <div className="center-box py-20">
            <Loading />
          </div>
        ) : unavailable ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            CS 응대 로그가 아직 서버에 반영되지 않았습니다.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            조회 기간에 남긴 메모가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">작성일시</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">작성자</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">단위</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">대상</th>
                  <th className="px-4 py-3 font-medium">내용</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">메모장</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const badge = unitBadge(item);
                  return (
                    <tr
                      key={item._id}
                      className={item.adminOnly ? 'bg-amber-50/50' : 'hover:bg-gray-50'}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                        {fmtDateTime(item.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className="text-xs font-medium text-gray-700">
                          {item.byName}
                        </span>
                        <span
                          className={`ml-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                            item.byRole === 'Admin'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-teal-100 text-teal-700'
                          }`}
                        >
                          {item.byRole === 'Admin' ? '관리자' : '마리'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                        {item.adminOnly && (
                          <span className="ml-1 inline-flex rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-amber-800">
                            관리자 전용
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs">
                        {item.coupon && (
                          <div className="font-mono text-gray-700">
                            {couponCode(item)}
                          </div>
                        )}
                        {item.delivery && (
                          <div className="font-mono text-gray-700">
                            {item.delivery.code}
                          </div>
                        )}
                        {item.coupon?.itemName && (
                          <div className="text-gray-500">{item.coupon.itemName}</div>
                        )}
                        {(() => {
                          const badge = redeemBadge(item.coupon?.redeemStatus);
                          return badge ? (
                            <span
                              className={`mt-0.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          ) : null;
                        })()}
                        {item.order && (
                          <div className="text-gray-500">
                            {item.order.receiverName ?? '-'}
                            {item.order.receiverPhoneNumber && (
                              <span className="ml-1 text-gray-400">
                                {fmtPhone(item.order.receiverPhoneNumber)}
                              </span>
                            )}
                          </div>
                        )}
                        {!item.coupon && !item.delivery && !item.order && (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="max-w-[520px] px-4 py-2.5">
                        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                          {item.body}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {memoTargetOf(item) ? (
                          <button
                            type="button"
                            onClick={() => setModalItem(item)}
                            className="rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
                            title="이 건의 메모장 열기 (이어서 남기기)"
                          >
                            열기
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지 이동 */}
        {!unavailable && total > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>페이지당</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border px-2 py-1 text-xs"
              >
                {[50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}건
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                이전
              </button>
              <span className="text-xs text-gray-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {modalItem && modalTarget && (
        <CsMemoModal
          title={
            modalItem.giftCoupon
              ? `쿠폰 ${couponCode(modalItem)} · CS 응대 메모`
              : modalItem.giftDelivery
                ? `배송 ${modalItem.delivery?.code ?? ''} · CS 응대 메모`
                : '주문 CS 응대 메모'
          }
          subtitle={
            modalItem.order
              ? `주문자 ${modalItem.order.receiverName ?? '-'} · ${fmtPhone(
                  modalItem.order.receiverPhoneNumber,
                )}`
              : undefined
          }
          target={modalTarget}
          onClose={() => setModalItem(null)}
          zIndexClassName="z-50"
        />
      )}
    </div>
  );
};
